import { NervaUserData, TrialPlan } from 'hydra-data-types';
import React, { ReactElement, useEffect } from 'react';
import { LayoutScreenConfig, ScreenType } from '../../../config/FlowTypes';
import { hasReferralInURL } from '../../../config/AssessmentUtils';
import { ActionType } from '../../../store/ActionTypes';
import { dispatchPapScoreResult } from '../../../store/pap/PapActions';
import { store, useAppStore } from '../../../store/Store';
import { Analytics } from '../../../util/Analytics';
import { Environment } from '../../../util/Environment';
import { ScreenWrapper } from '../../styles/Containers';
import { NavigationProps } from '../FlowNavigation';
import { PresentationLayout } from '../layouts/PresentationLayout';
import { calculatePapScore, PapTier } from './PapScoring';

export type PapDiscountResultLayoutConfig = Record<string, never>;

export type PapDiscountResultScreenConfig<ScreenNames extends string> = LayoutScreenConfig<
  ScreenType.PapDiscountResult,
  PapDiscountResultLayoutConfig,
  ScreenNames
>;

type Props<ScreenNames extends string> = NavigationProps<
  PapDiscountResultScreenConfig<ScreenNames>,
  ScreenNames
>;

const PAP_COUPON_CODES = {
  [PapTier.Moderate]: { prod: 'PAPWA50', dev: 'PAP50DEV' },
  [PapTier.Full]: { prod: 'PAPWA100', dev: 'PAP100DEV' },
} as const;

const PAP_ONEOFF_STANDARD_COUPON = { prod: 'PAPONEOFF25', dev: 'PAPONEOFF25DEV' } as const;

type PapCouponCode =
  | (typeof PAP_COUPON_CODES)[keyof typeof PAP_COUPON_CODES]['prod' | 'dev']
  | (typeof PAP_ONEOFF_STANDARD_COUPON)['prod' | 'dev'];

function papCouponCode(tier: PapTier.Moderate | PapTier.Full): PapCouponCode {
  return Environment.PRODUCTION ? PAP_COUPON_CODES[tier].prod : PAP_COUPON_CODES[tier].dev;
}

function oneOffStandardCouponCode(): PapCouponCode {
  return Environment.PRODUCTION ? PAP_ONEOFF_STANDARD_COUPON.prod : PAP_ONEOFF_STANDARD_COUPON.dev;
}

type TierConfig = {
  content: string[];
  couponCode?: PapCouponCode;
  discount: `${25 | 50 | 100}%`;
  buttonTitle: 'Apply discount' | 'Continue';
};

const PAP_TIERS: Record<PapTier, TierConfig> = {
  [PapTier.Standard]: {
    content: [
      'Thank you for completing the assessment.',
      'Based on your responses, you qualify for our standard patient assistance: 25% discount.',
    ],
    // Referred patients by default get 25% off so we don't need to dispatch another code as it's already applied.
    discount: '25%',
    buttonTitle: 'Continue',
  },
  [PapTier.Moderate]: {
    content: [
      'Thank you for completing the assessment.',
      "Based on your responses, you qualify for additional support. You'll receive a 50% discount through our Patient Assistance Program.",
    ],
    couponCode: papCouponCode(PapTier.Moderate),
    discount: '50%',
    buttonTitle: 'Apply discount',
  },
  [PapTier.Full]: {
    content: [
      'Thank you for completing the assessment.',
      'Based on your responses, you qualify for full coverage at no cost.',
    ],
    couponCode: papCouponCode(PapTier.Full),
    discount: '100%',
    buttonTitle: 'Apply discount',
  },
};

function getNonReferredTierConfig(tier: PapTier): TierConfig {
  const base = PAP_TIERS[tier];
  if (tier === PapTier.Standard) {
    return { ...base, couponCode: oneOffStandardCouponCode(), buttonTitle: 'Apply discount' };
  }
  return base;
}

function getPapAnalyticsVariant(isNonReferred: boolean) {
  return isNonReferred ? 'subscriptionNoInsurance' : 'referred';
}

export const PapDiscountResultScreen = <ScreenNames extends string>(
  props: Props<ScreenNames>
): ReactElement => {
  const userData = useAppStore((state) => state.userData);
  const isNonReferred = !hasReferralInURL();
  const { tier, score } = calculatePapScore(userData as NervaUserData, isNonReferred);
  const tierMessage = isNonReferred ? getNonReferredTierConfig(tier) : PAP_TIERS[tier];

  useEffect(function lockPapFlowAndTrackCompletion() {
    // Clear the page stack so the back button cannot navigate to previous PAP screens
    store.dispatch({
      type: ActionType.SET_PAGE,
      page: props.screenName,
      resetHistory: true,
    });

    Analytics.track({
      event: Analytics.Event.o_pap_completed,
      score,
      discount: tierMessage.discount,
      variant: getPapAnalyticsVariant(isNonReferred),
    });
  }, []);

  function onContinue() {
    dispatchPapScoreResult({ score, tier });

    if (tierMessage.couponCode !== undefined) {
      store.dispatch({
        type: ActionType.SET_COUPON_CODE,
        couponCode: tierMessage.couponCode,
      });
    }

    if (tier === PapTier.Full) {
      store.dispatch({
        type: ActionType.SET_SELECTED_TRIAL_PLAN,
        trialPlan: TrialPlan.None,
      });
    }
    props.navigation.next(props, true);
  }

  return (
    <ScreenWrapper>
      <PresentationLayout
        content={tierMessage.content.map((text) => ({ type: 'text' as const, content: text }))}
        primaryButtonTitle={tierMessage.buttonTitle}
        onPressPrimaryButton={onContinue}
      />
    </ScreenWrapper>
  );
};
